import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import { weddingFeatureUpsellCaptions } from './helpers';
import { getAsset } from '../../../../../../util/assets';
import useFeaturePermission from '../../../../services/featurePermissions';
import Button from '../../../../../../components/global/Button';
import { WeddingEventUpgradeModal } from '../../../../components/WeddingEventUpgrade';
import usePurchaseEventCheckout from '../../../../components/PurchaseEvent/hooks/usePurchaseEventCheckout';
import { getErrorString } from '../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import { STRIPE_CHECKOUT_MODAL } from '../../../../../../components/global/StripeCheckout';
import { formatPriceForDisplay } from '../../../../../../util/format-money';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../constants/account-referrals';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import Spinner from '../../../../../../components/global/Spinner';
import ArrowRight from '../../../../../../components/icons/ArrowRight';
import { checkoutPrice as checkoutPriceQuery } from '../../../../../../graphql/queries/checkout';
import { MeQuery } from '../../../../../../graphql/queries/account';

const propTypes = {
  // DO NOT go with purchased event ID.
  // Upgrades require album-folder-ids, as such,
  // always make sure we have this available.
  albumFolderId: PropTypes.string,
  featureId: PropTypes.string,
  onCheckoutSuccess: PropTypes.func,
  onClose: PropTypes.func,
  loadingComponent: PropTypes.node,
  banner: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  illustration: PropTypes.node,
};

const WeddingEventFeatureUpsell = ({
  albumFolderId,
  featureId,
  title,
  subtitle,
  illustration,
  onCheckoutSuccess,
  onClose,
  banner,
  className,
  loadingComponent = (
    <div className="d-flex justify-content-center p-5">
      <Spinner />
    </div>
  ),
}) => {
  const openAlert = useOpenAlert();
  const openModal = useOpenModal();
  const { onCheckout, submitting } = usePurchaseEventCheckout({
    albumFolderId,
    onCheckoutSuccess: () => {
      onCheckoutSuccess?.();

      openAlert({
        type: 'success',
        title: 'Congratulations!',
        message:
          'Your plan has been upgraded! You can now enjoy newly unlocked features!',
      });

      onClose?.();
    },
    onCheckoutError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
  });

  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const purchasedEventFeatures = useFeaturePermission({
    albumFolderId,
    features: featureId ? [featureId] : undefined,
  });

  const multiplePlansSupported =
    !featureId || purchasedEventFeatures[featureId].plans?.length > 1;

  const eventItem = purchasedEventFeatures[featureId]?.plans?.[0];

  const { data: { me: user } = {} } = useQuery(MeQuery);
  const { data: { checkoutPrice } = {}, loading: loadingPricing } = useQuery(
    checkoutPriceQuery,
    {
      variables: {
        event_items: [
          {
            event_item_id: eventItem?.id,
            quantity: 1,
            album_folder_id: albumFolderId,
          },
        ],
      },
      fetchPolicy: 'network-only',
      skip: !!multiplePlansSupported || !eventItem,
    },
  );

  const onUpgrade = () => {
    if (multiplePlansSupported) {
      setUpgradeModalOpen(true);
      return;
    }

    openModal(STRIPE_CHECKOUT_MODAL, {
      buttonLabel: `Pay $${formatPriceForDisplay(eventItem?.price.value)}`,
      // If the event is FREE, will not require CC
      cardRequired: false,
      onSuccess: token =>
        onCheckout({
          eventItemId: eventItem.id,
          token,
        }),
      checkoutPriceVariables: ({ promo_code: promoCode }) => {
        return {
          event_items: [
            {
              event_item_id: eventItem.id,
              quantity: 1,
              album_folder_id: albumFolderId,
            },
          ],
          promo_code: promoCode,
        };
      },
      accountReferralTypes: [
        ACCOUNT_REFERRAL_TYPE.WEDDINGS_50_USD_OFF,
        ACCOUNT_REFERRAL_TYPE.WEDDINGS_100_PC_OFF_ONCE,
        ACCOUNT_REFERRAL_TYPE.WEDDINGS,
      ],
      defaultName: user?.name ?? '',
      defaultEmail: user?.email_address ?? '',
    });
  };

  const classes = classNames({
    'wedding-event-feature-upsell-banner': banner,
    'wedding-event-feature-upsell-container': !banner,
    'wedding-event-feature-upsell-container-with-illustration':
      !banner && !!weddingFeatureUpsellCaptions[featureId]?.illustration,
    [className]: !!className,
  });

  if (purchasedEventFeatures.loading) {
    return <>{loadingComponent}</>;
  }

  const upgradeModal = (
    <WeddingEventUpgradeModal
      onClose={
        !multiplePlansSupported
          ? () => {
              onClose?.();
              setUpgradeModalOpen(false);
            }
          : () => setUpgradeModalOpen(false)
      }
      forceOpen={upgradeModalOpen}
      albumFolderId={albumFolderId}
      onCheckoutSuccess={purchasedEventItem => {
        if (
          !purchasedEventFeatures[featureId]?.plans?.find(
            plan => plan.id === purchasedEventItem.id,
          )
        ) {
          openAlert({
            type: 'success',
            title: 'Congratulations!',
            message: (
              <>
                <p>Your plan has been upgraded!</p>
                <p>
                  {' '}
                  However, the selected plan does not include this feature. We
                  thank you for your purchase and hope you enjoy your newly
                  unlocked features.
                </p>
                <p>You can always unlock this feature later on.</p>
              </>
            ),
          });

          onClose?.();
          return;
        }

        openAlert({
          type: 'success',
          title: 'Congratulations!',
          message:
            'Your plan has been upgraded! You can now enjoy newly unlocked features!',
        });
        onCheckoutSuccess?.();
        onClose?.();
      }}
    />
  );

  if (banner) {
    return (
      <>
        <Button btnStyle="reset" className={classes} onClick={onUpgrade}>
          <img
            src={getAsset('/diamond-orange.svg')}
            alt="Unlock your potential"
            width="28"
            className="mr-1"
          />
          <h3>{title ?? weddingFeatureUpsellCaptions[featureId]?.title}</h3>
          <ArrowRight className="ml-1" color="#fff" />
        </Button>
        {upgradeModal}
      </>
    );
  }

  return (
    <div className={classes}>
      {(illustration ??
        weddingFeatureUpsellCaptions[featureId]?.illustration) && (
        <img
          alt="Unlock feature"
          src={getAsset(
            illustration ??
              weddingFeatureUpsellCaptions[featureId]?.illustration,
          )}
        />
      )}
      <div className="wedding-event-feature-upsell-content">
        <img
          src={getAsset('/diamond-orange.svg')}
          alt="Unlock your potential"
          width="37"
          className="mb-2"
        />
        <h2>{title ?? weddingFeatureUpsellCaptions[featureId]?.title}</h2>
        {subtitle ?? weddingFeatureUpsellCaptions[featureId]?.subtitle}

        {(!featureId || purchasedEventFeatures?.id) && (
          <>
            <div className="mt-2">
              {multiplePlansSupported ? (
                <Button onClick={onUpgrade}>Upgrade now</Button>
              ) : (
                <Button
                  onClick={onUpgrade}
                  disabled={submitting || loadingPricing}
                >
                  {submitting || loadingPricing ? (
                    'Loading...'
                  ) : (
                    <>
                      Upgrade ($
                      {formatPriceForDisplay(checkoutPrice?.total_price?.value)}
                      )
                    </>
                  )}
                </Button>
              )}
            </div>
            {!multiplePlansSupported && (
              <div className="mt-4">
                <hr />
                <Button
                  onClick={() => setUpgradeModalOpen(true)}
                  btnStyle="link"
                  disabled={submitting}
                  className="text-bold"
                  btnSize="sm"
                >
                  View all WaldoWed plans →
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {upgradeModal}
    </div>
  );
};

WeddingEventFeatureUpsell.propTypes = propTypes;

export default WeddingEventFeatureUpsell;
